// streaks.js
// Single source of truth for streak storage + date math.
// All streaks live in localStorage (no backend yet). Token grant/spend is folded
// into the once-per-day update so there is no separate "inventory" to manage.

export const FREEZE_CAP = 2;
export const FREEZE_EARN_EVERY = 10;

// localStorage keys
export const PLAY_STREAK_KEY = 'topingoStreak';        // play streak (kept name to migrate old data)
export const DAILY_STREAK_KEY = 'topingoDailyStreak';  // daily streak (uses freeze)
export const WIN_STREAK_KEY = 'topingoWinStreak';      // win streak (play-based, no dates)

const emptyDateStreak = () => ({
  currentStreak: 0,
  bestStreak: 0,
  lastSolvedDate: null,
  freezeTokens: 0,
});

const emptyWinStreak = () => ({ current: 0, best: 0 });

/**
 * Whole-day difference between two YYYY-MM-DD strings, using UTC day boundaries
 * so it is correct across month/year edges (unlike Date#getDate()).
 * @returns {number} todayStr - prevStr in days. NaN-safe: returns Infinity if no prev date.
 */
export function dayDiff(prevDateStr, todayStr) {
  if (!prevDateStr) return Infinity;
  const [py, pm, pd] = prevDateStr.split('-').map(Number);
  const [ty, tm, td] = todayStr.split('-').map(Number);
  const prev = Date.UTC(py, pm - 1, pd);
  const today = Date.UTC(ty, tm - 1, td);
  return Math.round((today - prev) / 86400000);
}

export function readStreak(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return key === WIN_STREAK_KEY ? emptyWinStreak() : emptyDateStreak();
    const parsed = JSON.parse(raw);
    if (key === WIN_STREAK_KEY) {
      return { current: parsed.current || 0, best: parsed.best || 0 };
    }
    // migrate legacy objects that lack freezeTokens
    return {
      currentStreak: parsed.currentStreak || 0,
      bestStreak: parsed.bestStreak || 0,
      lastSolvedDate: parsed.lastSolvedDate || null,
      freezeTokens: parsed.freezeTokens || 0,
    };
  } catch (e) {
    console.error('readStreak failed for', key, e);
    return key === WIN_STREAK_KEY ? emptyWinStreak() : emptyDateStreak();
  }
}

export function writeStreak(key, obj) {
  try {
    localStorage.setItem(key, JSON.stringify(obj));
  } catch (e) {
    console.error('writeStreak failed for', key, e);
  }
}

/**
 * Time-based streak update. Call once when a qualifying event happens on `today`
 * (play streak: any game finished; daily streak: any daily won).
 *
 * - diff 0  → already counted today, no-op (kills replay/reload double-count)
 * - diff 1  → consecutive day, increment
 * - diff >1 → gap. If useFreeze and enough tokens, spend (diff-1) and hold streak.
 *             Otherwise break (reset to 1).
 * Milestone: every FREEZE_EARN_EVERY days grants 1 freeze (cap FREEZE_CAP), once.
 *
 * @param {object} prev   previous streak object
 * @param {string} today  YYYY-MM-DD
 * @param {{useFreeze?: boolean}} opts
 */
export function applyDailyStreak(prev, today, { useFreeze = false } = {}) {
  const diff = dayDiff(prev.lastSolvedDate, today);
  if (diff === 0) return prev; // already counted today

  let { currentStreak, freezeTokens } = prev;

  if (!prev.lastSolvedDate) {
    currentStreak = 1;
  } else if (diff === 1) {
    currentStreak += 1;
  } else if (useFreeze && freezeTokens >= diff - 1) {
    freezeTokens -= diff - 1; // spend tokens to cover the gap; streak survives, no increment
  } else {
    currentStreak = 1; // gap too big / no tokens → break
  }

  // grant on crossing a milestone, exactly once (guarded by a real advance)
  if (useFreeze && currentStreak % FREEZE_EARN_EVERY === 0 && currentStreak > prev.currentStreak) {
    freezeTokens = Math.min(freezeTokens + 1, FREEZE_CAP);
  }

  return {
    currentStreak,
    bestStreak: Math.max(currentStreak, prev.bestStreak),
    lastSolvedDate: today,
    freezeTokens,
  };
}

/** Play-based win streak: increment on win. */
export function applyWin(prev) {
  const current = prev.current + 1;
  return { current, best: Math.max(current, prev.best) };
}

/** Play-based win streak: reset on loss. */
export function applyLoss(prev) {
  return { current: 0, best: prev.best };
}

// ---- One-time migration / backfill -----------------------------------------

const BACKFILL_FLAG = 'topingoStreakBackfillV1';
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Scan localStorage for daily-challenge results and return the set of ISO dates
 * on which the user won at least one daily (any topic). Daily games are saved
 * under `topingo_daily_<topic>_<YYYY-MM-DD>` as { guesses, isGameOver, isWin }.
 */
export function collectDailyWinDates() {
  const dates = new Set();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('topingo_daily_')) continue;
    const date = key.slice(-10); // trailing YYYY-MM-DD
    if (!ISO_DATE_RE.test(date)) continue;
    try {
      const v = JSON.parse(localStorage.getItem(key));
      if (v && v.isWin) dates.add(date);
    } catch (e) {
      // ignore malformed entries
    }
  }
  return dates;
}

/**
 * Reconstruct a daily streak from a set/array of win dates.
 * - bestStreak: longest consecutive run in history.
 * - currentStreak: run ending at the latest win, but only "alive" if that win
 *   was today or yesterday (otherwise the streak is broken → 0).
 */
export function computeDailyStreakFromWins(winDates, today) {
  const sorted = [...(winDates instanceof Set ? winDates : new Set(winDates))].sort();
  if (sorted.length === 0) {
    return { currentStreak: 0, bestStreak: 0, lastSolvedDate: null };
  }

  let best = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    run = dayDiff(sorted[i - 1], sorted[i]) === 1 ? run + 1 : 1;
    if (run > best) best = run;
  }

  const latest = sorted[sorted.length - 1];
  const gapToToday = dayDiff(latest, today);
  let current = 0;
  if (gapToToday === 0 || gapToToday === 1) {
    current = 1;
    for (let i = sorted.length - 1; i > 0; i--) {
      if (dayDiff(sorted[i - 1], sorted[i]) === 1) current += 1;
      else break;
    }
  }

  return { currentStreak: current, bestStreak: best, lastSolvedDate: latest };
}

/**
 * One-time migration, safe to call on every app load:
 *  - Sanitizes the legacy play-streak date (old code stored toDateString(),
 *    which the new ISO-based dayDiff can't parse) so it restarts cleanly.
 *  - Backfills the daily streak from historical daily wins, once.
 */
export function runStreakMigration(today) {
  // Sanitize legacy non-ISO play-streak date format.
  try {
    const play = JSON.parse(localStorage.getItem(PLAY_STREAK_KEY));
    if (play && play.lastSolvedDate && !ISO_DATE_RE.test(play.lastSolvedDate)) {
      play.lastSolvedDate = null;
      localStorage.setItem(PLAY_STREAK_KEY, JSON.stringify(play));
    }
  } catch (e) {
    // ignore
  }

  if (localStorage.getItem(BACKFILL_FLAG)) return;

  // Only backfill if no daily streak has been accrued under the new system yet.
  const existing = readStreak(DAILY_STREAK_KEY);
  const untouched = existing.currentStreak === 0 && existing.bestStreak === 0 && !existing.lastSolvedDate;
  if (untouched) {
    const wins = collectDailyWinDates();
    if (wins.size > 0) {
      const { currentStreak, bestStreak, lastSolvedDate } = computeDailyStreakFromWins(wins, today);
      const freezeTokens = Math.min(Math.floor(currentStreak / FREEZE_EARN_EVERY), FREEZE_CAP);
      writeStreak(DAILY_STREAK_KEY, { currentStreak, bestStreak, lastSolvedDate, freezeTokens });
    }
  }

  localStorage.setItem(BACKFILL_FLAG, '1');
}
